import React from 'react';
import { Billboard, Html } from '@react-three/drei';
import {
  ohtTipChipColors,
  phaseChipColors,
  phaseChipDisplayLabel,
  type OhtTipChip,
  type SimulationPhaseChip,
} from '../../utils/simulationPhaseUi';

export type OhtPhaseIndicator3DProps = {
  status: SimulationPhaseChip;
  /** Richer action chip (Pick / Drop / Hold) — overrides badge colours. */
  tipChip?: OhtTipChip;
  /** Override badge text — defaults to the legend phase name. */
  label?: string;
  opacity?: number;
  /** Model scale multiplier from OhtVehicle3DModel. */
  scale: number;
  vScale: number;
  pivotX: number;
  pivotY: number;
  width: number;
  height: number;
  /** World Y of the top of the vehicle body (badge floats above this). */
  bodyTopY?: number;
  showBadge?: boolean;
};

/**
 * Legend-matched OHT phase visuals — floating status chip above the vehicle.
 * Colours align with SimulationLegend (Travel / Deadhead / Wait / Idle).
 */
export const OhtPhaseIndicator3D: React.FC<OhtPhaseIndicator3DProps> = React.memo(({
  status,
  tipChip,
  label,
  scale: S,
  pivotX,
  pivotY,
  bodyTopY,
  showBadge = true,
}) => {
  const colors = tipChip ? ohtTipChipColors(tipChip) : phaseChipColors(status);
  const phaseName = phaseChipDisplayLabel(status);
  const badgeText = label?.trim() || phaseName;

  const bodyTop = bodyTopY ?? 0.5 + 4.5 * S;

  return (
    <group name="OhtPhaseIndicator">
      {showBadge ? (
        <Billboard follow position={[pivotX, bodyTop + 1.4 * S, pivotY]}>
          <Html center distanceFactor={10} zIndexRange={[40, 0]} style={{ pointerEvents: 'none' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              {badgeText !== phaseName ? (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 5,
                    backgroundColor: phaseChipColors(status).bg,
                    color: phaseChipColors(status).fg,
                    fontSize: 9,
                    fontWeight: 700,
                    fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    boxShadow: '0 1px 4px rgba(15,23,42,0.25)',
                  }}
                >
                  {phaseName}
                </span>
              ) : null}
              <span
                style={{
                  display: 'inline-block',
                  padding: '3px 10px',
                  borderRadius: 6,
                  backgroundColor: colors.bg,
                  color: colors.fg,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                  letterSpacing: '0.02em',
                  lineHeight: 1.2,
                  boxShadow: '0 2px 8px rgba(15,23,42,0.35)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  whiteSpace: 'nowrap',
                }}
              >
                {badgeText}
              </span>
            </div>
          </Html>
        </Billboard>
      ) : null}
    </group>
  );
});

export default OhtPhaseIndicator3D;
